
import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MasterService } from '../../../../../../../../services/master.service'
import { StoreService } from '../../../../../../../../services/store.service'
import { MessageService } from '../../../../../../../../services/message.service'
import * as languageLibrary from '../../../../../../../../services/language'
import { formatCurrency } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import moment from 'moment';

import { SALE_TYPES } from "src/app/constants/sale";

@Component({
    selector: 'payments-terms',
    templateUrl: './payments-terms.component.html',
    styleUrls: ['./payments-terms.component.scss'],
    standalone: false
})
export class PaymentsTermsComponent implements OnInit, OnChanges {
    /**  */
    @Input() saleId: number = 0

    /** */
    @Input() saleType: number = 0

    // Indicates whether or not the sale is unlocked and can accept changes.
    @Input() isUnLock: boolean = true

    /** Contract/Interest state date */
    @Input() sold: string | null = null

    /** Stage: Quote or Sale */
    @Input() stage: string | null = null

    /**  */
    @Output() paymentsChanged: EventEmitter<Object> = new EventEmitter()

    //  An associative array of sale types with the id as the key
    protected saleTypes: Record<number, string> = SALE_TYPES

    /** A hash (lookup) table of (statusId, status name) */
    protected statuses = {}

    /** A hash (lookup) table of (categoryId, category name) */
    protected categories = {}

    /**  */
    protected loading: boolean = false

    protected saving: boolean = false

    /**  */
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    /**  */
    protected words = languageLibrary.language

    protected form: FormGroup | null = null

    get termable (): boolean { return ((this.saleType) && (['Outside', 'BHPH'].includes(this.saleTypes[this.saleType]))) }

     /**
     * Helper method to consistently get the list of form fees as an Array.
     */
    get payments() {
        return this.form.controls['payments'] as FormArray;
    }

    /** Compute the total amount of all down payments. */
    get total(): number {
        let total = 0
        for (const payment of this.payments.controls) {
            const id = parseInt(payment.get('id').value)
            if (!id || (id < 0)) { // only include saved/commited downs
                continue
            }

            total += clean(payment.get('amount').value)
        }

        return total
    }

    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, private fb: FormBuilder) {
        this.form = this.fb.group({
            payments: this.fb.array([])
        })
    }

    ngOnInit() {
        this.fetch()
    }

    ngOnDestroy(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        //Add '${implements OnChanges}' to the class
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.saleId) {
            this.fetch()
        }

        if (changes.isUnLock) {
            this.form.disable({emitEvent: false})
            if (this.isUnLock) {
                this.form.enable({emitEvent: false})
            }
        }

        if (changes.sold) {
            this.computeDueDates()
        }
    }

    /*
        Reload and clean the inputs
    */
    public reset = (): void => {
        this.form.reset()
        this.payments.clear()
    }

    public fetch = () => {
        if (isNaN(this.saleId) || (this.saleId <= 0)) {
            return
        }

        this.reset()

        this.loading = true
        this.master.get(`payments/expected?sale=${this.saleId}&sort=due|ASC&categories=deposit,initial%20down,deferred%20down`, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (!res.data) {
                return
            }

            const statusCollection = res.data.statusList
            if (statusCollection && (statusCollection.length > 0)) {
                for (let status of statusCollection) {
                    this.statuses[status.id] = status.name
                }
            }

            const categoryCollection = res.data.categories
            if (categoryCollection && (categoryCollection.length > 0)) {
                for (let category of categoryCollection) {
                    this.categories[category.id] = category.name
                }
            }

            if (!res.data.payments || (res.data.payments.length <= 0)) {
                return
            }

            for (let payment of res.data.payments) {
                const amount = parseFloat(payment['amount'])
                const status = payment['status'].toLowerCase()
                const paid = (status !== 'unpaid')

                const item = this.fb.group({
                    id: [parseInt(payment['id']), []],
                    saleId: [this.saleId, [Validators.required]],
                    category: [payment['category'], [Validators.required]], // 1 = Initial Down, 2 = Deferred Down
                    status: [payment['status'], []],
                    type: [payment['type'], []], // 1 = Down payment
                    amount: [formatCurrency(amount), []],
                    due: [payment['due'], []],
                    reference: [payment['reference'] || null, []],
                    memo: [payment['memo'] || null, []],
                    paid: [paid, []],
                    number: [payment['number'] || 0, []]
                })

                if (paid) {
                    item.disable()
                }

                this.payments.push(item)
            }

            this.emit(false)
        })
    }

    /**
     * Let any listeners know that the sale down payments changed.
     * @param {boolean} [save = true] Indicates whether or not the recipient of the event needs to recompute or handle an actual change (not just a load).
     */
    public emit = (save: boolean = true) => {
        if (!this.payments) {
            return
        }

        let initial = 0
        let deferred = 0
        let deposit = 0
        let last = null // last deferred/down payment due date

        for (let payment of this.payments.controls) {
            const id = parseInt(payment.get('id').value)
            if (!id || (id < 0)) { // only include saved/commited payments
                continue
            }

            const category = payment.get('category').value
            switch (category.toLowerCase()) {
                case 'deposit':
                    deposit += clean(payment.get('amount').value)
                    break
                case 'initial down':
                    initial += clean(payment.get('amount').value)
                    break
                case 'deferred down':
                    deferred += clean(payment.get('amount').value)
                    break
            }

            const due = moment(payment.get('due').value)
            if (last == null) {
                last = due.format('yyyy-MM-DD')
                continue
            }

            if (moment(last).isAfter(due)) {
                continue
            }

            last = due.format('yyyy-MM-DD')
        }

        this.ms.channelComponents$.emit({message: 'downsChanged', section: 'downs', value: { value: (initial + deferred + deposit), deposit: deposit, initial: initial, deferred: deferred, last: last, save: save }})
    }

    /**
     * Save all payments that are pending a save.
     */
    public saveAll = (): Promise<void> => {
        if (this.saving) {
            return
        }

        for (const payment of this.payments.controls) {
            if (payment.invalid || payment.pristine) {
                continue
            }

            this.upsert(payment as FormGroup)
        }

        this.emit()
    }

    /**
     * Save (upsert) a payment from the form array
     * @param index The index of the payment to save
     */
    public save = (index: number): Promise<void> => {
        if ((index === undefined) || (index === null) || (index < 0) || (index > this.payments.length - 1)) {
            return
        }

        if (this.saving) {
            return
        }

        const payment = this.payments.controls[index] as FormGroup
        this.upsert(payment)
    }

    /**
     * Determine if the API call should be an insert or
     * modify and call the proper method.
     * @param payment
     * @returns
     */
    private upsert = (payment: FormGroup) => {
        const id = parseInt(payment.get('id').value)
        if ((isNaN(id)) || (id < 0)) {
            return
        }

        if (id === 0) {
            this.insert(payment)
            return
        }

        this.modify(id, payment)
    }

    /**
     * Add a new sale down payment to the current sale.
     * @param payment Down payment meta data to add to the sale.
     */
    private insert = (payment: FormGroup): Promise<void> => {
        if (!this.saleId || !payment) {
            return
        }

        const payload = {
            saleId: this.saleId,
            category: payment.get('category').value,
            status: payment.get('status').value,
            type: payment.get('type').value,
            amount: clean(payment.get('amount').value),
            due: payment.get('due').value,
            reference: payment.get('reference').value,
            memo: payment.get('memo').value,
            number: payment.get('number').value
        }

        this.saving = true
        this.master.post(`sales/${this.saleId}/payments`, {payload: payload}, res => {
            this.saving = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            payment.markAsPristine()

            if (!res.data || !res.data.payments || (res.data.payments.length <= 0)) {
                return
            }

            payment.get('id').setValue(res.data.payments[0].id)
            this.emit()
        })
    }

    /**
     * Modify an existing down payment for the current sale.
     * @param id Unique internal identtiy of the exising down payment.
     * @param payment Down payment meta data to modify the existing one with.
     */
    private modify = (id: number, payment: FormGroup): Promise<void> => {
        if (!id || !this.saleId || !payment || (id <= 0)) {
            return
        }

        if (payment.get('paid').value) {
            this.ms.sendMessage("alert", { type: "danger", text: 'Unable to modify. Payment already partially or fully paid.' })
            return
        }

        const payload = {
            saleId: this.saleId,
            category: payment.get('category').value,
            status: payment.get('status').value,
            type: payment.get('type').value,
            amount: clean(payment.get('amount').value),
            due: payment.get('due').value,
            reference: payment.get('reference').value,
            memo: payment.get('memo').value,
            number: payment.get('number').value
        }

        this.saving = true
        this.master.put(`sales/${this.saleId}/payments/${id}`, {payload: payload}, res => {
            this.saving = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            payment.markAsPristine()
            this.emit()
        })
    }

    /**
     * Add a new sale down payment to the array.
     * @param e
     */
    public add = (e) => {
        if ((this.payments) && (this.payments.length > 0) && !this.termable) { // only one deposit allowed.
            return
        }

        let due = new Date(this.sold + ' 00:00:00')
        if (this.payments && this.payments.length > 0) {
            const prevDue = new Date(`${this.payments.controls[this.payments.length - 1].get('due').value}T00:00:00`)
            due = new Date(prevDue.getFullYear(), prevDue.getMonth(), prevDue.getDate() + 7, 0, 0, 0, 0)
        }

        let category = 'Deferred Down'
        let depositExists = this.payments.controls.find(payment => payment.value.category === 'Deposit' ? true : false)

        if ((this.payments.length <= 0) && ((this.stage == 'Quote') || (!this.termable))) {
            category = 'Deposit'
        }

        if (((this.payments.length <= 0) && ((this.stage !== 'Quote') && this.termable)) ||
            ((this.payments.length === 1) && depositExists)) {
            category = 'Initial Down'
        }

        let number = 0
        if ((this.payments.length > 0) && (category === 'Deferred Down')) {
            const deferreds = this.payments.controls.filter(payment => payment.value.category === 'Deferred Down')
            number = deferreds.length + 1
        }

        this.payments.push(this.fb.group({
            id: [0, []],
            saleId: [this.saleId, [Validators.required]],
            category: [category, [Validators.required]],
            status: ['Unpaid', []],
            type: ['Payment', []],
            amount: [formatCurrency(0), []],
            due: [moment(due).format('yyyy-MM-DD'), []],
            reference: [null, []],
            memo: [null, []],
            paid: [false, []],
            number: [number, []]
        }))
    }

    /**
     * Remove a sale down payment from the form array
     * @param index The index of the payment to remove
     */
    public discard = (index: number): void => {
        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }

        const id = parseInt(this.payments.at(index).get('id').value)
        if (!id || isNaN(id) || id <= 0) {
            this.payments.removeAt(index)
            return
        }

        if (this.payments.at(index).get('paid').value) {
            this.ms.sendMessage("alert", { type: "danger", text: 'Unable to delete. Payment already partially or fully paid.' })
            return
        }

        this.master.discard(`sales/${this.saleId}/payments/${id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.payments.removeAt(index)
            this.emit(false)
        })
    }

    /**
    * When the sale date changes lets compute the due dates of any existing down payments.
    */
    private computeDueDates(): void {
        if (!this.isUnLock) {
            return
        }

        if (!this.payments || (this.payments.length <= 0)) {
            return
        }

        let due = moment(this.sold)
        for (const payment of this.payments.controls) {
            if (due.isSame(payment.get('due').value)) {
                return // if initial down matches sold then we are good.
            }

            payment.get('due').setValue(due.format('yyyy-MM-DD'))
            payment.markAsDirty()

            due = due.add(7, 'days')
        }

        this.saveAll()
    }

}
